import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowRight, Calculator } from "lucide-react";
import { api, setPageMeta, setJsonLd, catSlug, SITE_BASE } from "@/lib/api";
import { Reveal } from "@/components/Motion";
import { ChecklistMagnet } from "@/components/ChecklistMagnet";
import { SectionVideo } from "@/components/SectionVideo";

const CATEGORIES = ["All", "Start Running", "5K", "10K", "Half Marathon", "Marathon", "Ultra", "HYROX", "Ironman", "Speed", "Training", "Nutrition", "Recovery", "Mindset", "Longevity", "Gear", "Community", "Dallas"];
const PER_PAGE = 24;

export default function TrainingHub() {
  const { cat, pageNum } = useParams();
  const [params] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = useMemo(() => {
    if (cat) return CATEGORIES.find((c) => catSlug(c) === cat) || "All";
    return params.get("category") || "All";
  }, [cat, params]);
  const page = Math.max(1, parseInt(pageNum || "1", 10) || 1);

  useEffect(() => {
    const base = category === "All"
      ? "Running Training Guides & Plans | Ninja Run Club"
      : `${category} Training Guides for Runners | Ninja Run Club`;
    const desc = category === "All"
      ? "74+ evidence-backed running guides and free training plans: 5K to marathon, HYROX, nutrition, recovery, and speed work. Written for runners, reviewed by coaches."
      : `Every Ninja Run Club guide on ${category.toLowerCase()} training: practical plans, honest advice, and evidence-backed workouts for runners at every level.`;
    setPageMeta(page > 1 ? `${base.replace(" | Ninja Run Club", "")} — Page ${page} | Ninja Run Club` : base, desc);
  }, [category, page]);

  useEffect(() => {
    setLoading(true);
    const q = category !== "All" ? { category, limit: 200 } : { limit: 200 };
    api.get("/articles", { params: q }).then((r) => setArticles(r.data)).finally(() => setLoading(false));
  }, [category]);

  const shown = category === "All" ? articles.slice((page - 1) * PER_PAGE, page * PER_PAGE) : articles;
  const totalPages = category === "All" ? Math.max(1, Math.ceil(articles.length / PER_PAGE)) : 1;

  useEffect(() => {
    if (!shown.length) return;
    return setJsonLd("training-jsonld", {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: category === "All" ? "Running training guides" : `${category} training guides`,
      numberOfItems: shown.length,
      itemListElement: shown.map((a, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: a.title,
        item: `${SITE_BASE}/training/${a.slug}`,
      })),
    });
  }, [shown, category]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="bg-night min-h-screen">
      <header className="relative overflow-hidden px-5 lg:px-10 pt-16 pb-10 border-b border-white/10">
        <SectionVideo src="/video/run-16.mp4" srcMobile="/video/run-16-mobile.mp4" poster="/video/run-16-poster.jpg" opacity={0.24} testid="training-hero-video" />
        <div className="relative">
        <Reveal>
          <p className="font-mono-label text-white/40">THE TRAINING HUB · TRAIN FOR WHAT IS NEXT</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-none">
            {category === "All" ? "RUNNING TRAINING GUIDES & PLANS." : `${category.toUpperCase()} TRAINING GUIDES & PLANS.`}
          </h1>
          <p className="text-white/50 text-sm max-w-2xl mt-4">
            {articles.length || "74"}+ evidence-backed guides covering every distance from your first 5K to ultras and HYROX, plus nutrition,
            recovery, and speed work. Each guide gives a direct answer up top, then the depth to act on it. No unlocked potential. Just work that works.
          </p>
        </Reveal>
        </div>
      </header>

      <nav aria-label="Training categories" data-testid="training-category-filters" className="sticky top-16 z-40 bg-night/90 backdrop-blur-xl border-b border-white/10 px-5 lg:px-10 py-4 flex gap-2 overflow-x-auto">
        {CATEGORIES.map((c) => (
          <Link
            key={c}
            to={c === "All" ? "/training" : `/training/category/${catSlug(c)}`}
            data-testid={`training-category-${c.toLowerCase().replace(/\s/g, "-")}`}
            className={`font-mono-label px-4 py-2.5 border whitespace-nowrap transition-colors duration-200 ${
              category === c ? "bg-white text-ink border-white" : "border-white/20 text-white/60 hover:border-white"
            }`}
          >
            {c}
          </Link>
        ))}
      </nav>

      <div className="px-5 lg:px-10 py-10">
        {loading ? (
          <p className="font-mono-label text-white/40 py-16 text-center">LOADING...</p>
        ) : shown.length === 0 ? (
          <div data-testid="training-empty" className="border border-white/10 p-14 text-center my-10">
            <p className="font-display text-3xl">NOTHING HERE YET.</p>
            <p className="text-white/50 text-sm mt-3">This category is on the editorial calendar. Check the full library meanwhile.</p>
            <Link to="/training" className="font-mono-label link-underline text-white/60 inline-block mt-4">BACK TO ALL GUIDES</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10" data-testid="training-articles-grid">
            {shown.map((a) => (
              <Link key={a.slug} to={`/training/${a.slug}`} data-testid={`article-card-${a.slug}`} className="group bg-night p-8 lg:p-10 hover:bg-white hover:text-ink transition-colors duration-300 flex flex-col">
                <p className="font-mono-label text-white/40 group-hover:text-ink/50">{a.category} · {a.read_time} MIN READ</p>
                <h2 className="font-display text-2xl lg:text-3xl mt-4 leading-tight flex-1">{a.title}</h2>
                <p className="text-sm text-white/50 group-hover:text-ink/60 mt-4">{a.excerpt}</p>
                <p className="font-mono-label mt-6 text-white/40 group-hover:text-ink/60">UPDATED {a.updated}</p>
              </Link>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <nav aria-label="Guide pages" data-testid="training-pagination" className="flex flex-wrap justify-center gap-2 mt-10">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <Link
                key={n}
                to={n === 1 ? "/training" : `/training/page/${n}`}
                data-testid={`training-page-${n}`}
                aria-current={n === page ? "page" : undefined}
                className={`font-mono-label border px-4 py-2.5 transition-colors duration-200 ${
                  n === page ? "bg-white text-ink border-white" : "border-white/20 text-white/60 hover:border-white"
                }`}
              >
                PAGE {n}
              </Link>
            ))}
          </nav>
        )}

        <Link to="/plan" data-testid="training-plan-banner" className="group mt-12 border border-white bg-white text-ink p-8 lg:p-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 hover:bg-night hover:text-white transition-colors duration-300">
          <div>
            <p className="font-mono-label opacity-50">NEW · THE PLAN BUILDER</p>
            <p className="font-display text-2xl lg:text-4xl mt-2">FIVE QUESTIONS. A CUSTOM TRAINING BLOCK.</p>
            <p className="text-sm opacity-60 mt-2">5K to marathon, HYROX to Ironman. Week-by-week, free, instant.</p>
          </div>
          <span className="font-mono-label flex items-center gap-2 shrink-0">BUILD MY PLAN <ArrowRight size={14} /></span>
        </Link>

        <Link to="/tools" data-testid="training-tools-banner" className="group mt-6 border border-white/15 p-8 lg:p-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 hover:bg-white hover:text-ink transition-colors duration-300">
          <div className="flex items-start gap-4">
            <Calculator size={28} className="mt-1" />
            <div>
              <p className="font-display text-2xl lg:text-4xl">TWENTY-ONE FREE TOOLS.</p>
              <p className="text-sm opacity-60 mt-2">Pace calculator, race predictor, HYROX splits, training zones, and more.</p>
            </div>
          </div>
          <span className="font-mono-label flex items-center gap-2">OPEN THE TOOLS <ArrowRight size={14} /></span>
        </Link>

        <div className="mt-6">
          <ChecklistMagnet source="checklist-training-hub" />
        </div>
      </div>
    </div>
  );
}
